import { FC, useContext, useEffect, useMemo, useState } from 'react';

import { useLazyQuery } from '@apollo/client';
import {
  Button,
  DateRangePicker,
  InfoBlock,
  Label,
  Table,
} from '@smartshell/ui-kit';
import dayjs from 'dayjs';
import { Column } from 'react-table';

import { useSharedDateRange, useWindowResize } from 'hooks';
import { LocalizationContext } from 'localization';
import { formatDateForServer } from 'utils';

import { ChartBlock } from './ChartBlock';
import {
  GENERATE_VISITORS_REPORT,
  GENERATE_VISITORS_REPORT_FILE,
  IVisitorsReportFileResponse,
  IVisitorsReportRequest,
  IVisitorsReportResponse,
} from './schema';
import { ESlice, TNormalizedItem } from './types';
import { formatPeople, getFormattedDate } from './utils';
import s from './Visitors.module.scss';

export const Visitors: FC = () => {
  const { t, lang } = useContext(LocalizationContext);

  const COLUMNS: Array<Column<TNormalizedItem>> = useMemo(
    () => [
      {
        accessor: 'date',
        Header: t('date'),
        Cell: ({ value }) => getFormattedDate('D MMM YYYY, dd')(value),
        sortType: (rowA, rowB) => {
          const [year1, day1, month1] = rowA.original.date;
          const [year2, day2, month2] = rowB.original.date;

          if (year1 < year2) return 1;
          if (year1 > year2) return -1;
          if (month1 < month2) return 1;
          if (month1 > month2) return -1;
          if (day1 < day2) return 1;
          if (day1 > day2) return -1;

          return 0;
        },
      },
      {
        accessor: 'clients',
        Header: t('clients'),
        Cell: ({
          value,
          row: {
            original: { newClients },
          },
        }) => (
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            {formatPeople(value, t)}
            {!!newClients && (
              <Label
                text={`+${newClients} ${t('clients.new')}`}
                color="green"
                rounded
              />
            )}
          </div>
        ),
        disableResizing: true,
        width: 182,
      },
      {
        accessor: 'clientSessions',
        Header: t('seances.clients'),
        Cell: ({ value }) => `${value} ${t('pcs')}`,
        disableResizing: true,
        width: 162,
      },
      {
        accessor: 'guests',
        Header: t('guests'),
        Cell: ({ value }) => formatPeople(value, t),
        disableResizing: true,
        width: 114,
      },
      {
        accessor: 'total',
        Header: t('visitors'),
        Cell: ({ value }) => formatPeople(value, t),
        disableResizing: true,
        width: 139,
      },
      {
        id: 'seances',
        accessor: ({ clientSessions, guestSessions }) =>
          clientSessions + guestSessions,
        Header: t('seances'),
        Cell: ({ value }: { value: number }) => `${value} ${t('pcs')}`,
        disableResizing: true,
        width: 126,
      },
    ],
    [t]
  );

  const {
    startDate,
    endDate,
    periodType,
    offset,
    setStartDate,
    setEndDate,
    setPeriodType,
    setOffset,
  } = useSharedDateRange();
  const [slice, setSlice] = useState<ESlice>(ESlice.DAY);

  const [error, setError] = useState<string | null>(null);

  const [generateReport, { data: response, loading, called, variables }] =
    useLazyQuery<IVisitorsReportResponse, IVisitorsReportRequest>(
      GENERATE_VISITORS_REPORT,
      {
        fetchPolicy: 'no-cache',
        onError: (error) => {
          setError(error.message);
        },
      }
    );

  const [generateReportFile, { loading: reportFileLoading }] = useLazyQuery<
    IVisitorsReportFileResponse,
    IVisitorsReportRequest
  >(GENERATE_VISITORS_REPORT_FILE, {
    fetchPolicy: 'no-cache',
    onCompleted: (response) => {
      if (!response) return;

      open(response.uniqueUsersReport.url, '_self');
    },
    onError: (error) => {
      setError(error.message);
    },
  });

  useEffect(() => {
    if (variables?.input) {
      generateReport({
        variables: { input: { ...variables.input, slice } },
      });
    }
  }, [slice]);

  const normalizedData = useMemo<Array<TNormalizedItem>>(
    () =>
      response?.uniqueUsersReport.data.map(({ values }, index) => {
        const [
          clients,
          guests,
          total,
          newClients,
          clientSessions,
          guestSessions,
        ] = values;
        const date = response.uniqueUsersReport.keys[index];

        return {
          guests,
          clients,
          total,
          date,
          newClients,
          clientSessions,
          guestSessions,
        };
      }) ?? [],
    [response]
  );

  const totalsData = useMemo(() => {
    const totalsData = {
      total: 0,
      totalGuests: 0,
      totalClients: 0,
      totalNewClients: 0,
      totalClientSeances: 0,
    };
    response?.uniqueUsersReport.summary.forEach((item) => {
      if (item.title === 'total') totalsData.total = item.value;
      if (item.title === 'users') totalsData.totalClients = item.value;
      if (item.title === 'guests') totalsData.totalGuests = item.value;
      if (item.title === 'new') totalsData.totalNewClients = item.value;
      if (item.title === 'user_sessions')
        totalsData.totalClientSeances += item.value;
    });

    return totalsData;
  }, [response]);

  const size = useWindowResize();

  return (
    <div className={s.container}>
      {error && (
        <InfoBlock title={t('errors.error')} message={error} direction="row" />
      )}
      <div className={s.generate}>
        <DateRangePicker
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
          lastValidDate={dayjs().format()}
          firstValidDate={dayjs('2022-03-01T00:00:00').format()}
          lang={lang}
          periodType={periodType}
          setPeriodType={setPeriodType}
          offset={offset}
          setOffset={setOffset}
        />
        <div className={s.buttons_block}>
          <Button
            onClick={() => {
              setError(null);

              const input: IVisitorsReportRequest['input'] = {
                from: formatDateForServer(dayjs(startDate)),
                to: formatDateForServer(dayjs(endDate)),
                slice,
              };

              return generateReport({ variables: { input } });
            }}
            disabled={loading}
          >
            {t('reports.generate')}
          </Button>
          <Button
            onClick={() => generateReportFile({ variables })}
            disabled={!variables?.input || reportFileLoading}
            className={s.export}
            iconName="IncomeIcon"
            variant="outline"
          >
            {size.width < 1864 && size.width > 960 ? t('csv') : t('export.csv')}
          </Button>
        </div>
      </div>
      <div className={s.data}>
        <ChartBlock
          data={normalizedData}
          loading={loading}
          called={called}
          slice={slice}
          setSlice={setSlice}
          {...totalsData}
        />
        <Table
          data={normalizedData}
          columns={COLUMNS}
          isLoading={loading}
          className={s.table}
          emptyDataPlaceholder={t(
            called ? 'report.placeholder' : 'report.placeholder.emptyDates'
          )}
        />
        {size.width < 960 && (
          <InfoBlock
            className={s.mobile_info}
            title={t('mobile.placeholder')}
            iconName="ComputerIcon"
            color="blue"
          />
        )}
      </div>
    </div>
  );
};
